import React, {useState} from "react";
import NiceModal, {useModal} from "@ebay/nice-modal-react";
import {Dialog, DialogActions, DialogContent, DialogTitle, Switch} from "@mui/material";
import {
  Box,
  Card,
  Checkbox,
  Colors,
  Container,
  FormControlLabel,
  IconAlert,
  IconButton,
  IconClose,
  Text,
} from "@bounce/shared/ui";
import {Button, MenuItem, Select, TextField} from "../../ui/components";
import {ParamSchema} from "../../apis/requests/segments/types";
import {useGetLastTriggerPayload} from "../../apis/requests/triggers/getLastTriggerPayload";
import {TriggerCondition, TriggerInteractionType, TriggerRule} from "../../apis/requests/triggers/types";
import {ChannelsSection, SectionTitle, TimingSection} from "./ConditionSections";
import {listCardSx} from "../../ui/components/cardStyles";
import {makeRule, normalizeRuleValue, ruleError} from "./helpers";
import SegmentedToggle from "./SegmentedToggle";
import {FieldLabel, mutedButtonSx} from "./triggerUi";
import TriggerRuleRow from "./TriggerRuleRow";
import {useTriggerTemplates} from "./useTriggerTemplates";

export type ConditionDialogResult =
  | {action: "save"; condition: TriggerCondition; priority: number}
  | {action: "delete"};

type Props = {
  condition: TriggerCondition;
  isNew: boolean;
  priority: number;
  priorityCount: number;
  lockedPriorities?: number[];
  parameters: ParamSchema[];
  interactionType: TriggerInteractionType;
  onSubmit: (result: ConditionDialogResult) => void;
};

const MATCH_OPTIONS: {value: TriggerCondition["match_mode"]; label: string}[] = [
  {value: "all", label: "Match all"},
  {value: "any", label: "Match any"},
];

const sectionSx = {
  p: "18px 20px",
  display: "flex",
  flexDirection: "column",
  alignItems: "stretch",
  gap: 1.5,
  backgroundColor: Colors.tertiary._100,
  border: `1px solid ${Colors.tertiary._300}`,
  borderRadius: "8px",
  boxShadow: "none",
} as const;

const ConditionDialog = NiceModal.create(
  ({
    condition: initial,
    isNew,
    priority: initialPriority,
    priorityCount,
    lockedPriorities = [],
    parameters,
    interactionType,
    onSubmit,
  }: Props) => {
    const modal = useModal();
    const [condition, setCondition] = useState<TriggerCondition>(initial);
    const [priority, setPriority] = useState(initialPriority);
    const [sendText, setSendText] = useState(initial.text_template_id !== null);
    const [issue, setIssue] = useState<string | null>(null);
    const [showLastPayload, setShowLastPayload] = useState(false);
    const {templates} = useTriggerTemplates();
    const {data: lastPayload} = useGetLastTriggerPayload({interactionType});

    const isDefault = condition.is_default;
    const update = (patch: Partial<TriggerCondition>) => setCondition((current) => ({...current, ...patch}));
    const updateRule = (index: number, rule: TriggerRule) =>
      update({rules: condition.rules.map((r, i) => (i === index ? rule : r))});
    const removeRule = (index: number) => update({rules: condition.rules.filter((_, i) => i !== index)});

    const close = () => modal.hide();
    const finish = (result: ConditionDialogResult) => {
      onSubmit(result);
      modal.hide();
    };

    const onSave = () => {
      const rules = condition.rules.map((rule) => ({
        ...rule,
        value: normalizeRuleValue(parameters, rule),
      }));
      const send = condition.outcome === "send";
      const problem =
        (!condition.name.trim() && "Give the condition a name") ||
        (!isDefault && rules.length === 0 && "Add at least one rule") ||
        rules.map((rule) => ruleError(rule, rule.value)).find(Boolean) ||
        (send && !condition.email_template_id && "Pick an email template") ||
        (send && sendText && !condition.text_template_id && "Pick a text template or untick Text") ||
        null;
      if (problem) {
        setIssue(problem);
        return;
      }
      finish({
        action: "save",
        priority,
        condition: {
          ...condition,
          name: condition.name.trim(),
          rules: isDefault ? [] : rules,
          email_template_id: send ? condition.email_template_id : null,
          text_template_id: send && sendText ? condition.text_template_id : null,
        },
      });
    };

    return (
      <Dialog
        open={modal.visible}
        onClose={close}
        slotProps={{
          transition: {onExited: () => modal.remove()},
          paper: {sx: {borderRadius: "16px", maxWidth: 900, width: "100%", backgroundColor: Colors.bw.white}},
        }}
        maxWidth={false}
      >
        <DialogTitle sx={{p: "20px 24px", display: "flex", justifyContent: "space-between", alignItems: "center"}}>
          <Text size={20} variant="interDisplay600" sx={{color: Colors.grey._800}}>
            {isNew ? "New condition" : "Condition"}
          </Text>
          <IconButton
            onClick={close}
            size="small"
            sx={{color: Colors.grey._500, "&:hover": {backgroundColor: Colors.tertiary._200}}}
          >
            <IconClose width={20} height={20} />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{p: "0 24px 24px", display: "flex", flexDirection: "column", gap: 2}}>
          <Card sx={sectionSx}>
            <SectionTitle>Details</SectionTitle>
            <Container row fullWidth sx={{gap: 1.5, alignItems: "flex-start", flexWrap: "nowrap"}}>
              <Box sx={{flex: 2, minWidth: 0}}>
                <FieldLabel>Name *</FieldLabel>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="High balance, pre-legal"
                  value={condition.name}
                  onChange={(e) => update({name: e.target.value})}
                />
              </Box>
              <Box sx={{flex: 1, minWidth: 0}}>
                <FieldLabel>Priority</FieldLabel>
                {isDefault ? (
                  <>
                    <TextField size="small" fullWidth disabled value="Last" />
                    <Text size={12} sx={{color: Colors.grey._600, mt: 0.5}}>
                      Runs when nothing above it matched.
                    </Text>
                  </>
                ) : (
                  <Select fullWidth size="small" value={priority} onChange={(e) => setPriority(Number(e.target.value))}>
                    {Array.from({length: priorityCount}, (_, i) => i + 1).map((option) => (
                      <MenuItem key={option} value={option} disabled={lockedPriorities.includes(option)}>
                        {option === 1 ? "1 — evaluated first" : option}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              </Box>
            </Container>
            {!isDefault && (
              <FormControlLabel
                sx={{m: 0, alignItems: "flex-start"}}
                control={
                  <Checkbox
                    checked={condition.locked}
                    onChange={(e) => update({locked: e.target.checked})}
                    sx={{p: 0, mr: 1, mt: "2px"}}
                  />
                }
                label={
                  <Box>
                    <Text size={14} variant="inter500">
                      Lock this condition
                    </Text>
                    <Text size={12} sx={{color: Colors.grey._600}}>
                      It keeps its place, and it can&apos;t be edited or deleted until someone unlocks it.
                    </Text>
                  </Box>
                }
              />
            )}
          </Card>

          <Card sx={sectionSx}>
            <Box sx={{display: "flex", alignItems: "center", justifyContent: "space-between"}}>
              <SectionTitle>Matches when</SectionTitle>
              {!isDefault && (
                <SegmentedToggle
                  value={condition.match_mode}
                  options={MATCH_OPTIONS}
                  onChange={(match_mode) => update({match_mode})}
                />
              )}
            </Box>
            {isDefault ? (
              <Text size={13} sx={{color: Colors.grey._700}}>
                The default condition always matches. It runs when none of the conditions above it matched.
              </Text>
            ) : (
              <>
                {condition.rules.map((rule, index) => (
                  <TriggerRuleRow
                    key={index}
                    rule={rule}
                    parameters={parameters}
                    onChange={(next) => updateRule(index, next)}
                    onRemove={() => removeRule(index)}
                    showLabels={index === 0}
                  />
                ))}
                <Box>
                  <Button
                    buttonType="outlined-primary"
                    size="small"
                    onClick={() => update({rules: [...condition.rules, makeRule(parameters)]})}
                  >
                    + Add rule
                  </Button>
                </Box>
                <Box sx={{...listCardSx, p: 1.5}}>
                  <Box sx={{display: "flex", alignItems: "center", justifyContent: "space-between"}}>
                    <Text size={13} sx={{color: Colors.grey._700}}>
                      Last seen {interactionType} payload
                    </Text>
                    <Switch
                      size="small"
                      checked={showLastPayload}
                      onChange={(e) => setShowLastPayload(e.target.checked)}
                    />
                  </Box>
                  {showLastPayload && (
                    <Box
                      component="pre"
                      sx={{
                        m: 0,
                        mt: 0.5,
                        fontSize: 12,
                        fontFamily: "monospace",
                        color: Colors.grey._800,
                        whiteSpace: "pre-wrap",
                        maxHeight: 220,
                        overflow: "auto",
                      }}
                    >
                      {lastPayload?.occurred_at
                        ? JSON.stringify(lastPayload.data, null, 2)
                        : "No interaction of this type yet."}
                    </Box>
                  )}
                </Box>
              </>
            )}
          </Card>

          <Card sx={sectionSx}>
            <TimingSection
              timing={condition.timing}
              onChange={(timing) => update({timing})}
              interactionType={interactionType}
            />
          </Card>

          <Card sx={sectionSx}>
            <ChannelsSection
              interactionType={interactionType}
              condition={condition}
              templates={templates}
              sendText={sendText}
              onSendTextChange={setSendText}
              onChange={update}
            />
          </Card>
        </DialogContent>
        <DialogActions
          sx={{p: "16px 24px", justifyContent: "space-between", borderTop: `1px solid ${Colors.tertiary._200}`}}
        >
          <Container row sx={{gap: 1.5, alignItems: "center", minHeight: 32}}>
            {!isNew && !isDefault && !condition.locked && (
              <Button
                buttonType="outlined-primary"
                size="small"
                onClick={() => finish({action: "delete"})}
                sx={{
                  borderColor: Colors.red._300,
                  color: Colors.red._800,
                  "&:hover": {backgroundColor: Colors.red._100},
                }}
              >
                Delete condition
              </Button>
            )}
            {issue && (
              <Container row sx={{gap: 1, alignItems: "center"}}>
                <IconAlert width={18} height={18} color={Colors.red._900} />
                <Text size={13} sx={{color: Colors.red._900}}>
                  {issue}
                </Text>
              </Container>
            )}
          </Container>
          <Container row sx={{gap: 1}}>
            <Button buttonType="outlined-primary" size="small" onClick={close} sx={mutedButtonSx}>
              Cancel
            </Button>
            <Button buttonType="contained-primary" size="small" onClick={onSave}>
              {isNew ? "Add condition" : "Update condition"}
            </Button>
          </Container>
        </DialogActions>
      </Dialog>
    );
  }
);

export default ConditionDialog;
